/**
 * 取得穩定列 ID
 * - 優先取後端回傳的 `id`
 * - 若沒有則退回 `orderItemID`
 *
 * @param {Object} row - 單筆商品資料
 * @param {number|string} [row.id] - 後端生成的唯一識別碼
 * @param {number|string} [row.orderItemID] - 訂單商品的 ID
 * @returns {string|number} 唯一識別 ID
 */
export const getId = (row) => row.id || row.orderItemID;

/**
 * 判斷商品是否可被選取（可退費）
 * - 已全額退過的商品不可再選
 *   - 僅價可調：`salePrice === returnPrice`
 *   - 可調數量：剩餘數量 <= 0
 *
 * @param {Object} row - 單筆商品資料
 * @param {boolean} row.onlyAllowancePrice - 是否為「僅價可調」模式
 * @param {number} row.salePrice - 商品單價
 * @param {number} row.returnPrice - 已退金額
 * @param {number} row.quantity - 原始數量
 * @param {number} row.returnQuantity - 已退數量
 * @param {Object} [opts={}] - 選項
 * @param {boolean} [opts.listDisabled=false] - 整份清單是否被禁用
 * @returns {boolean} 是否可選取
 */
export const isSelectable = (row, { listDisabled = false } = {}) => {
    if (listDisabled) return false;
    const salePrice = Number(row.salePrice) || 0;
    const returnPrice = Number(row.returnPrice) || 0;
    const qty = Number(row.quantity) || 0;
    const returnQty = Number(row.returnQuantity) || 0;
    if (row.onlyAllowancePrice) return salePrice !== returnPrice;
    return qty - returnQty > 0;
};

/**
 * 計算商品的上下限與預設值
 * - 僅價可調：允許金額 1..salePrice，數量固定 1
 * - 可調數量：允許數量 1..(剩餘數量)，價格鎖定 salePrice
 *
 * @param {Object} row - 單筆商品資料
 * @returns {{priceMin:number, priceMax:number, qtyMin:number, qtyMax:number, defaultPrice:number, defaultQty:number}}
 * - priceMin: 最小允許金額
 * - priceMax: 最大允許金額
 * - qtyMin: 最小允許數量
 * - qtyMax: 最大允許數量
 * - defaultPrice: 預設金額
 * - defaultQty: 預設數量
 */
export const boundsOf = (row) => {
    const salePrice = Number(row.salePrice) || 0;
    const qty = Number(row.quantity) || 0;
    const returnedQty = Number(row.returnQuantity) || 0;
    const remainQty = Math.max(0, qty - returnedQty);

    if (row.onlyAllowancePrice) {
        const already = Number(row.returnPrice) || 0;
        const defaultPrice = Math.max(1, salePrice - already);
        return {
            priceMin: 1,
            priceMax: salePrice,
            qtyMin: 1,
            qtyMax: 1,
            defaultPrice,
            defaultQty: 1,
        };
    }
    return {
        priceMin: salePrice,
        priceMax: salePrice,
        qtyMin: 1,
        qtyMax: Math.max(1, remainQty),
        defaultPrice: salePrice,
        defaultQty: Math.max(1, remainQty),
    };
};

/**
 * 驗證輸入的金額是否有效
 *
 * @param {Object} row - 單筆商品資料
 * @param {number|string} n - 使用者輸入的金額
 * @returns {boolean} 是否錯誤（true = 無效，false = 有效）
 */
export const validatePrice = (row, n) => {
    const { priceMin, priceMax } = boundsOf(row);
    const v = Number(n);
    return !(Number.isFinite(v) && v >= priceMin && v <= priceMax);
};

/**
 * 驗證輸入的數量是否有效
 *
 * @param {Object} row - 單筆商品資料
 * @param {number|string} n - 使用者輸入的數量
 * @returns {boolean} 是否錯誤（true = 無效，false = 有效）
 */
export const validateQty = (row, n) => {
    if (row.onlyAllowancePrice) return false;
    const { qtyMin, qtyMax } = boundsOf(row);
    const v = Number(n);
    return !(Number.isFinite(v) && v >= qtyMin && v <= qtyMax);
};

/**
 * 計算單筆商品的折讓小計
 *
 * @param {Object} row - 單筆商品資料
 * @param {number|string} [priceInput] - 使用者輸入的折讓金額（僅價可調）
 * @param {number|string} [qtyInput] - 使用者輸入的折讓數量（可調數量）
 * @returns {number} 折讓小計金額
 */
export const calcRowTotal = (row, priceInput, qtyInput) => {
    const b = boundsOf(row);
    if (row.onlyAllowancePrice) {
        const price = Number(priceInput || b.defaultPrice) || 0;
        return price * 1;
    }
    const qty = Number(qtyInput || b.defaultQty) || 0;
    const salePrice = Number(row.salePrice) || 0;
    return salePrice * qty;
}

/**
 * 計算單筆商品的最大可退金額與數量
 * @param {Object} row - 單筆商品資料
 * @returns {{ allowancePrice: number, allowanceQuantity: number, subtotal: number }}
 * - allowancePrice: 本次允許退的單價
 * - allowanceQuantity: 本次允許退的數量
 * - subtotal: 本次最大可退小計金額
 */
export const computeMaxRefundForRow = (row) => {
    const salePrice = Number(row.salePrice) || 0;
    const qty = Number(row.quantity) || 0;
    const returnedQty = Number(row.returnQuantity) || 0;
    const returnPrice = Number(row.returnPrice) || 0;

    if (row.onlyAllowancePrice) {
        const allowancePrice = Math.max(0, salePrice - returnPrice);
        return { allowancePrice, allowanceQuantity: 1, subtotal: allowancePrice };
    }
    const remainQty = Math.max(0, qty - returnedQty);
    const allowanceQuantity = remainQty;
    const subtotal = salePrice * remainQty;
    return { allowancePrice: salePrice, allowanceQuantity, subtotal };
}
